import { GraphsData } from '@/api/workOrders/types';
import { Box, Grid, Typography } from '@mui/material';
import { useMemo } from 'react';

import { GraphTitle, StyledCardMedia } from '../../styles';
import { INF_GRAPHS_FIELD_LABELS } from '../constants';
import { VariableDescriptionSection } from '../types';

interface GraphConfig {
  key: string;
  title: string;
}

interface InferenceGraphsProps {
  variableDescriptions: VariableDescriptionSection;
  graphs: GraphsData[];
}

export const FIXED_GRAPHS: GraphConfig[] = [
  { key: 'aderencia', title: INF_GRAPHS_FIELD_LABELS.adherence },
  { key: 'residuos', title: INF_GRAPHS_FIELD_LABELS.residuals },
  {
    key: 'curvaNormalReduzida',
    title: INF_GRAPHS_FIELD_LABELS.reducedNormalCurve,
  },
  { key: 'cook', title: INF_GRAPHS_FIELD_LABELS.cooksDistance },
  { key: 'valorTotal', title: INF_GRAPHS_FIELD_LABELS.totalValue },
];

export const generateVariableGraphs = (
  independentVariables: VariableDescriptionSection['independent_variables']
): GraphConfig[] => {
  return independentVariables
    .filter((variable) => variable.name.trim() !== '')
    .map((variable, index) => ({
      key: `variable_${index}_${variable.name
        .replace(/\s+/g, '_')
        .toLowerCase()}`,
      title: variable.name,
    }));
};

export function InferenceGraphs({
  variableDescriptions,
  graphs,
}: InferenceGraphsProps): JSX.Element {
  const allGraphs = useMemo(() => {
    const variableGraphs = generateVariableGraphs(
      variableDescriptions.independent_variables
    );
    return [...FIXED_GRAPHS, ...variableGraphs];
  }, [variableDescriptions.independent_variables]);

  const fixedTitleSet = useMemo(
    () => new Set(FIXED_GRAPHS.map((g) => g.title)),
    []
  );

  const dynamicApiGraphs = useMemo(
    () => graphs.filter((g) => !fixedTitleSet.has(g.title)),
    [graphs, fixedTitleSet]
  );

  const getSavedUrl = (
    graph: GraphConfig,
    index: number
  ): string | undefined => {
    const isFixed = index < FIXED_GRAPHS.length;
    if (isFixed) return graphs.find((g) => g.title === graph.title)?.file_url;
    return dynamicApiGraphs[index - FIXED_GRAPHS.length]?.file_url;
  };

  const renderGraphCard = (
    graphConfig: GraphConfig,
    index: number
  ): JSX.Element => {
    const { key, title } = graphConfig;
    const savedUrl = getSavedUrl(graphConfig, index);

    return (
      <Grid item xs={6} xl={4} key={key}>
        <Box mb={4}>
          <GraphTitle>{title}</GraphTitle>
          {savedUrl ? (
            <Box width="400px" margin="0 auto">
              <StyledCardMedia image={savedUrl} title={title} />
            </Box>
          ) : (
            <Typography color="text.secondary">-</Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box id="inf-graphs">
      <Typography my={2} fontWeight={600}>
        Gráficos Fixos
      </Typography>
      <Grid container spacing={2}>
        {FIXED_GRAPHS.map((graphConfig, index) =>
          renderGraphCard(graphConfig, index)
        )}
      </Grid>
      {allGraphs.length > FIXED_GRAPHS.length && (
        <>
          <Typography my={2} fontWeight={600}>
            Gráficos Dinâmicos
          </Typography>
          <Grid container spacing={2}>
            {allGraphs
              .slice(FIXED_GRAPHS.length)
              .map((graphConfig, index) =>
                renderGraphCard(graphConfig, FIXED_GRAPHS.length + index)
              )}
          </Grid>
        </>
      )}
    </Box>
  );
}
